import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUp, Copy, GripVertical, Sparkles, Trash2, Type } from "lucide-react";

const HANDLES = ["l", "r", "tl", "tr", "bl", "br"];
const pos = { l: "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize", r: "right-0 top-1/2 translate-x-1/2 -translate-y-1/2 cursor-ew-resize", tl: "left-0 top-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize", tr: "right-0 top-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize", bl: "left-0 bottom-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize", br: "right-0 bottom-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize" };
const SNAP = 8;
const GUIDE = "#FF4D8D";
const PAD_C = "#0EA5A5";
const MAR_C = "#F59E0B";
const EDGE = { t: "Top", r: "Right", b: "Bottom", l: "Left" };
const ARROW = { t: "↑", r: "→", b: "↓", l: "←" };

const px = (n) => `${Math.round(n)}px`;

const Guides = ({ data }) => {
  if (!data) return null;
  return (
    <div className="absolute inset-0 z-30 pointer-events-none" data-testid="align-guides">
      {data.lines.map((l, i) => l.axis === "v"
        ? <div key={i} data-testid="guide-v" className="absolute" style={{ left: l.x, top: l.y1, height: Math.max(1, l.y2 - l.y1), borderLeft: `1px dashed ${GUIDE}` }} />
        : <div key={i} data-testid="guide-h" className="absolute" style={{ top: l.y, left: l.x1, width: Math.max(1, l.x2 - l.x1), borderTop: `1px dashed ${GUIDE}` }} />)}
      {data.labels.map((b, i) => (
        <span key={i} data-testid="guide-label" className="absolute -translate-x-1/2 -translate-y-1/2 rounded px-1.5 py-0.5 font-mono text-[10px] leading-none text-white shadow" style={{ left: b.x, top: b.y, background: GUIDE }}>{b.text}</span>
      ))}
    </div>
  );
};

export const Canvas = ({ ed, onAskAI }) => {
  const { iframeRef, srcDoc, loadKey, onLoad, device, selected, hover, el, doc, tick, editing, setStyle, commit } = ed;
  const [drag, setDrag] = useState(null);
  const [move, setMove] = useState(null);
  const [drop, setDrop] = useState(null);
  const [guides, setGuides] = useState(null);
  const [spacing, setSpacing] = useState(null);
  const wrap = useRef(null);
  const rect = (id) => {
    const e = el(id); if (!e) return null;
    const r = e.getBoundingClientRect();
    const cs = doc()?.defaultView.getComputedStyle(e);
    const n = (p) => (cs ? parseFloat(cs[p]) || 0 : 0);
    return { x: r.left, y: r.top, w: r.width, h: r.height, tag: e.tagName.toLowerCase(), img: e.tagName === "IMG", only: e.getAttribute("data-lp-only"), pad: { t: n("paddingTop"), r: n("paddingRight"), b: n("paddingBottom"), l: n("paddingLeft") }, mar: { t: n("marginTop"), r: n("marginRight"), b: n("marginBottom"), l: n("marginLeft") } };
  };
  const sel = selected ? rect(selected) : null;
  const hov = hover && hover !== selected && !move ? rect(hover) : null;
  void tick;

  // ---------- resize ----------
  const startResize = (h) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const elx = el(selected); const d = doc(); if (!elx || !d) return;
    const r0 = elx.getBoundingClientRect();
    const parent = elx.parentElement;
    const img = elx.tagName === "IMG", ratio = r0.height / r0.width;
    const prev = { w: elx.style.width, h: elx.style.height };
    const x0 = e.clientX, y0 = e.clientY, w0 = r0.width, h0 = r0.height;
    let done = false;

    const candidates = () => {
      const list = [];
      if (!parent) return list;
      const pr = parent.getBoundingClientRect();
      list.push({ x: pr.right, r: pr }, { x: pr.left + pr.width / 2, r: pr }, { x: pr.left + pr.width / 3, r: pr }, { x: pr.left + pr.width / 4, r: pr });
      [...parent.children].forEach((s) => {
        if (s === elx || s.nodeType !== 1) return;
        const sr = s.getBoundingClientRect();
        if (sr.width < 2) return;
        list.push({ x: sr.right, r: sr }, { x: sr.left, r: sr }, { x: sr.left + sr.width / 2, r: sr });
      });
      return list;
    };

    const paint = (snap) => {
      const er = elx.getBoundingClientRect();
      const lines = [], labels = [];
      if (snap) {
        const y1 = Math.min(er.top, snap.r.top) - 12, y2 = Math.max(er.bottom, snap.r.bottom) + 12;
        lines.push({ axis: "v", x: snap.x, y1, y2 });
      }
      if (parent) {
        const pr = parent.getBoundingClientRect();
        const my = er.top + er.height / 2;
        if (er.left - pr.left > 3) { lines.push({ axis: "h", y: my, x1: pr.left, x2: er.left }); labels.push({ x: (pr.left + er.left) / 2, y: my - 10, text: px(er.left - pr.left) }); }
        if (pr.right - er.right > 3) { lines.push({ axis: "h", y: my, x1: er.right, x2: pr.right }); labels.push({ x: (er.right + pr.right) / 2, y: my - 10, text: px(pr.right - er.right) }); }
      }
      setGuides({ lines, labels });
    };

    const off = () => { const ir = iframeRef.current?.getBoundingClientRect(); return { l: ir?.left || 0, t: ir?.top || 0 }; };
    const apply = (cx, cy) => {
      if (done) return;
      let w = Math.max(20, w0 + (cx - x0) * (h.includes("l") ? -1 : 1));
      elx.style.width = px(w);
      let snap = null;
      const er = elx.getBoundingClientRect();
      for (const c of candidates()) if (Math.abs(er.right - c.x) < SNAP && (!snap || Math.abs(er.right - c.x) < Math.abs(er.right - snap.x))) snap = c;
      if (snap) { w = Math.max(20, w + (snap.x - er.right)); elx.style.width = px(w); }
      if (img && h.length === 2) elx.style.height = px(w * ratio);
      else if (h.length === 2 && !img) elx.style.height = px(Math.max(10, h0 + (cy - y0) * (h.includes("t") ? -1 : 1)));
      setDrag({ w: Math.round(w), snapped: !!snap });
      paint(snap);
    };
    const outer = (ev) => apply(ev.clientX, ev.clientY);
    const inner = (ev) => { const o = off(); apply(ev.clientX + o.l, ev.clientY + o.t); };
    const finish = (cancel) => {
      if (done) return;
      done = true;
      window.removeEventListener("mousemove", outer); window.removeEventListener("mouseup", onUp); window.removeEventListener("keydown", onKey);
      d.removeEventListener("mousemove", inner); d.removeEventListener("mouseup", onUp); d.removeEventListener("keydown", onKey);
      setDrag(null); setGuides(null);
      if (cancel) { elx.style.width = prev.w; elx.style.height = prev.h; return; }
      if (device === "mobile") { const w = elx.style.width, hh = elx.style.height; elx.style.width = prev.w; elx.style.height = prev.h; setStyle(selected, "width", w, { commitNow: false }); if (hh) setStyle(selected, "height", hh, { commitNow: false }); }
      commit();
    };
    function onUp() { finish(false); }
    function onKey(ev) { if (ev.key === "Escape") { ev.preventDefault(); finish(true); } }

    setDrag({ w: Math.round(w0), snapped: false });
    paint(null);
    window.addEventListener("mousemove", outer); window.addEventListener("mouseup", onUp); window.addEventListener("keydown", onKey);
    d.addEventListener("mousemove", inner); d.addEventListener("mouseup", onUp); d.addEventListener("keydown", onKey);
  };

  // ---------- move / reorder ----------
  const beginMove = (clientX, clientY, fromInner) => {
    const elx = el(selected); const d = doc();
    if (!elx || !d || editing) return;
    const off = () => { const r = iframeRef.current?.getBoundingClientRect(); return { l: r?.left || 0, t: r?.top || 0 }; };
    const toLocal = (e, inner) => { if (inner) return { x: e.clientX, y: e.clientY }; const o = off(); return { x: e.clientX - o.l, y: e.clientY - o.t }; };
    const o0 = off();
    const start = fromInner ? { x: clientX, y: clientY } : { x: clientX - o0.l, y: clientY - o0.t };
    const prev = { op: elx.style.opacity, pe: elx.style.pointerEvents, us: d.body.style.userSelect };
    let active = false, target = null, side = "before", last = start, scrollDir = 0;

    const paint = (t, r, sd) => {
      const lines = [], labels = [];
      const vw = d.documentElement.clientWidth;
      const y = sd === "before" ? r.top : r.bottom;
      lines.push({ axis: "h", y, x1: 0, x2: vw });
      lines.push({ axis: "v", x: r.left, y1: r.top - 14, y2: r.bottom + 14 }, { axis: "v", x: r.right, y1: r.top - 14, y2: r.bottom + 14 });
      labels.push({ x: r.left + r.width / 2, y: r.top + r.height / 2, text: `${t.tagName.toLowerCase()} ${Math.round(r.width)}×${Math.round(r.height)}` });
      const nb = sd === "before" ? t.previousElementSibling : t.nextElementSibling;
      if (nb && nb !== elx) {
        const nr = nb.getBoundingClientRect();
        const gap = sd === "before" ? r.top - nr.bottom : nr.top - r.bottom;
        if (gap > 2) {
          const gx = Math.min(r.left + 40, r.right - 40);
          lines.push({ axis: "v", x: gx, y1: Math.min(y, y + (sd === "before" ? -gap : gap)), y2: Math.max(y, y + (sd === "before" ? -gap : gap)) });
          labels.push({ x: gx, y: y + (sd === "before" ? -gap / 2 : gap / 2), text: `${px(gap)} de espaço` });
        }
      }
      setGuides({ lines, labels });
    };

    const compute = (p) => {
      last = p;
      if (!active) {
        if (Math.hypot(p.x - start.x, p.y - start.y) < 5) return;
        active = true;
        elx.style.opacity = "0.4"; elx.style.pointerEvents = "none"; d.body.style.userSelect = "none";
      }
      setMove({ x: p.x, y: p.y, tag: elx.tagName.toLowerCase() });
      const vh = d.documentElement.clientHeight;
      scrollDir = p.y < 60 ? -1 : p.y > vh - 60 ? 1 : 0;
      let t = d.elementFromPoint(p.x, p.y)?.closest("[data-lp]");
      while (t && (t === elx || elx.contains(t))) t = t.parentElement?.closest("[data-lp]");
      if (!t || !t.parentElement || t.tagName === "BODY") { target = null; setDrop(null); setGuides(null); return; }
      const r = t.getBoundingClientRect();
      side = p.y < r.top + r.height / 2 ? "before" : "after";
      target = t;
      setDrop({ x: r.left, y: side === "before" ? r.top : r.bottom, w: r.width, tag: t.tagName.toLowerCase(), side });
      paint(t, r, side);
    };

    const iv = setInterval(() => { if (active && scrollDir) { d.defaultView.scrollBy(0, scrollDir * 16); compute(last); } }, 16);
    const outer = (e) => compute(toLocal(e, false));
    const inner = (e) => compute(toLocal(e, true));
    const finish = (cancel) => {
      clearInterval(iv);
      window.removeEventListener("mousemove", outer); window.removeEventListener("mouseup", onUp); window.removeEventListener("keydown", onKey);
      d.removeEventListener("mousemove", inner); d.removeEventListener("mouseup", onUp); d.removeEventListener("keydown", onKey);
      elx.style.opacity = prev.op; elx.style.pointerEvents = prev.pe; d.body.style.userSelect = prev.us;
      const did = active && target && !cancel;
      setMove(null); setDrop(null); setGuides(null);
      if (active) ed.skipClick.current = true;
      if (did) { side === "before" ? target.before(elx) : target.after(elx); commit(); }
    };
    function onUp() { finish(false); }
    function onKey(e) { if (e.key === "Escape") finish(true); }

    window.addEventListener("mousemove", outer); window.addEventListener("mouseup", onUp); window.addEventListener("keydown", onKey);
    d.addEventListener("mousemove", inner); d.addEventListener("mouseup", onUp); d.addEventListener("keydown", onKey);
  };

  // ---------- margin / padding handles ----------
  const startSpacing = (kind, edge) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const elx = el(selected); const d = doc(); if (!elx || !d) return;
    const prop = kind + EDGE[edge];
    const base = parseFloat(d.defaultView.getComputedStyle(elx)[prop]) || 0;
    const prevInline = elx.style[prop];
    const x0 = e.clientX, y0 = e.clientY;
    const axis = edge === "t" || edge === "b" ? "y" : "x";
    const sign = kind === "padding" ? (edge === "t" || edge === "l" ? 1 : -1) : (edge === "t" || edge === "l" ? -1 : 1);
    let done = false;

    const apply = (cx, cy) => {
      if (done) return;
      const delta = axis === "y" ? cy - y0 : cx - x0;
      let val = Math.round(base + delta * sign);
      if (kind === "padding") val = Math.max(0, val);
      if (Math.abs(val - Math.round(val / SNAP) * SNAP) <= 2) val = Math.round(val / SNAP) * SNAP;
      setStyle(selected, prop, `${val}px`, { commitNow: false });
      setSpacing({ kind, edge, val, snapped: val % SNAP === 0 });
    };
    const outer = (ev) => apply(ev.clientX, ev.clientY);
    const inner = (ev) => { const ir = iframeRef.current?.getBoundingClientRect(); apply(ev.clientX + (ir?.left || 0), ev.clientY + (ir?.top || 0)); };
    const finish = (cancel) => {
      if (done) return;
      done = true;
      window.removeEventListener("mousemove", outer); window.removeEventListener("mouseup", onUp); window.removeEventListener("keydown", onKey);
      d.removeEventListener("mousemove", inner); d.removeEventListener("mouseup", onUp); d.removeEventListener("keydown", onKey);
      setSpacing(null);
      if (cancel) { setStyle(selected, prop, prevInline || "", { commitNow: false }); return; }
      commit();
    };
    function onUp() { finish(false); }
    function onKey(ev) { if (ev.key === "Escape") { ev.preventDefault(); finish(true); } }

    setSpacing({ kind, edge, val: base, snapped: false });
    window.addEventListener("mousemove", outer); window.addEventListener("mouseup", onUp); window.addEventListener("keydown", onKey);
    d.addEventListener("mousemove", inner); d.addEventListener("mouseup", onUp); d.addEventListener("keydown", onKey);
  };

  const moveRef = useRef(beginMove); moveRef.current = beginMove;
  useEffect(() => { ed.dragStart.current = (e) => moveRef.current(e.clientX, e.clientY, true); }, [ed.dragStart]);

  const Tool = ({ icon: I, title, onClick, testid, danger }) => <button data-testid={testid} title={title} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }} className={`h-7 w-7 grid place-items-center rounded hover:bg-white/15 ${danger ? "text-red-300" : ""}`}><I size={13} /></button>;

  return (
    <div className="flex-1 overflow-auto bg-[#eef1f6] p-6 dot-grid" data-testid="editor-canvas">
      <div ref={wrap} className={`relative mx-auto bg-white shadow-2xl transition-[width] duration-500 rounded-lg overflow-hidden ${move ? "cursor-grabbing" : ""}`} style={{ width: device === "mobile" ? 390 : "100%", height: "calc(100vh - 140px)" }}>
        <iframe key={loadKey} ref={iframeRef} title="canvas" srcDoc={srcDoc} onLoad={onLoad} className="w-full h-full border-0 bg-white" data-testid="editor-iframe" />
        {hov && <div className="absolute pointer-events-none border border-[var(--blue)]/60 rounded-sm" style={{ left: hov.x, top: hov.y, width: hov.w, height: hov.h }}><span className="absolute -top-5 left-0 bg-[var(--blue)]/70 text-white font-mono text-[9px] px-1.5 py-0.5 rounded">{hov.tag}</span></div>}

        <Guides data={guides} />

        {drop && (
          <div data-testid="drop-indicator" className="absolute z-30 pointer-events-none" style={{ left: drop.x, top: drop.y - 2, width: drop.w, height: 4 }}>
            <div className="h-1 w-full rounded-full bg-[var(--blue)] shadow-[0_0_0_3px_rgba(10,124,255,.2)]" />
            <span className="absolute -top-1.5 -left-1.5 h-4 w-4 rounded-full border-2 border-[var(--blue)] bg-white" />
            <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full border-2 border-[var(--blue)] bg-white" />
          </div>
        )}
        {move && <div data-testid="drag-ghost" className="absolute z-40 pointer-events-none px-2 py-1 rounded-md bg-[var(--ink)]/90 text-white font-mono text-[10px] shadow-xl" style={{ left: move.x + 14, top: move.y + 14 }}>{move.tag} {drop ? `→ ${drop.side === "before" ? "antes de" : "depois de"} ${drop.tag}` : "· solte sobre um bloco"}</div>}

        {sel && (
          <div className="absolute pointer-events-none" style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h }} data-testid="selection-box">
            <div className={`absolute inset-0 border-2 ${editing ? "border-dashed border-[var(--orange)]" : move ? "border-dashed border-[var(--blue)]/50" : "border-[var(--blue)]"} rounded-sm pointer-events-none`} />
            {sel.only && <span data-testid="variant-badge" className="absolute -top-5 right-0 font-mono text-[9px] px-1.5 py-0.5 rounded bg-[var(--orange)] text-white pointer-events-none">só {sel.only === "mobile" ? "celular" : "desktop"}</span>}
            {!editing && !move && HANDLES.map((h) => (
              <span key={h} data-testid={`handle-${h}`} onMouseDown={startResize(h)} className={`absolute ${pos[h]} h-6 w-6 grid place-items-center pointer-events-auto group`}>
                <span className="h-3 w-3 rounded-[3px] bg-white border-2 border-[var(--blue)] shadow-sm transition-transform group-hover:scale-[1.45]" />
              </span>
            ))}
            {drag && <span data-testid="resize-badge" className={`absolute -bottom-7 right-0 text-white font-mono text-[10px] px-2 py-1 rounded ${drag.snapped ? "" : "bg-[var(--ink)]"}`} style={drag.snapped ? { background: GUIDE } : undefined}>{drag.w}px{drag.snapped ? " · alinhado" : ""}</span>}

            {!editing && !move && !drag && sel.w >= 56 && sel.h >= 20 && (
              <>
                {spacing && (() => {
                  const k = spacing.kind === "padding", c = k ? PAD_C : MAR_C, v = Math.max(0, spacing.val);
                  const box = k
                    ? { t: { left: 0, top: 0, width: sel.w, height: v }, b: { left: 0, top: sel.h - v, width: sel.w, height: v }, l: { left: 0, top: 0, width: v, height: sel.h }, r: { left: sel.w - v, top: 0, width: v, height: sel.h } }[spacing.edge]
                    : { t: { left: 0, top: -v, width: sel.w, height: v }, b: { left: 0, top: sel.h, width: sel.w, height: v }, l: { left: -v, top: 0, width: v, height: sel.h }, r: { left: sel.w, top: 0, width: v, height: sel.h } }[spacing.edge];
                  return (
                    <>
                      <div className="absolute pointer-events-none" style={{ ...box, background: `${c}33`, outline: `1px dashed ${c}` }} />
                      <span data-testid="spacing-badge" className="absolute z-40 -translate-x-1/2 -translate-y-1/2 rounded px-1.5 py-0.5 font-mono text-[10px] leading-none text-white shadow pointer-events-none" style={{ left: box.left + box.width / 2, top: box.top + box.height / 2, background: c }}>
                        {k ? "preench." : "margem"} {ARROW[spacing.edge]} {spacing.val}px
                      </span>
                    </>
                  );
                })()}
                {["t", "r", "b", "l"].map((edge) => {
                  const p = sel.pad[edge], m = sel.mar[edge];
                  const vert = edge === "t" || edge === "b";
                  const padStyle = { t: { left: "50%", top: p }, b: { left: "50%", top: sel.h - p }, l: { top: "50%", left: p }, r: { top: "50%", left: sel.w - p } }[edge];
                  const marStyle = { t: { left: "70%", top: -Math.max(m, 9) }, b: { left: "30%", top: sel.h + Math.max(m, 9) }, l: { top: "70%", left: -Math.max(m, 9) }, r: { top: "30%", left: sel.w + Math.max(m, 9) } }[edge];
                  const shape = vert ? "h-[6px] w-7" : "w-[6px] h-7";
                  const cursor = vert ? "cursor-ns-resize" : "cursor-ew-resize";
                  return (
                    <span key={edge}>
                      <span data-testid={`pad-${edge}`} title={`Preenchimento ${ARROW[edge]} — arraste (Esc cancela)`} onMouseDown={startSpacing("padding", edge)}
                        className={`absolute ${shape} ${cursor} rounded-full pointer-events-auto -translate-x-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 hover:scale-125 transition-transform`} style={{ ...padStyle, background: PAD_C }} />
                      <span data-testid={`mar-${edge}`} title={`Margem ${ARROW[edge]} — arraste (Esc cancela)`} onMouseDown={startSpacing("margin", edge)}
                        className={`absolute ${shape} ${cursor} rounded-full pointer-events-auto -translate-x-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 hover:scale-125 transition-transform`} style={{ ...marStyle, background: MAR_C }} />
                    </span>
                  );
                })}
              </>
            )}
            {!editing && !move && (
              <div className="absolute -top-9 left-0 flex items-center gap-0.5 bg-[var(--ink)] text-white rounded-lg px-1 py-0.5 shadow-lg pointer-events-auto" style={{ top: sel.y < 44 ? sel.h + 6 : -36 }} data-testid="element-toolbar">
                <button data-testid="el-grip" title="Arraste para mover o elemento" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); beginMove(e.clientX, e.clientY, false); }} className="h-7 w-6 grid place-items-center rounded cursor-grab active:cursor-grabbing text-slate-300 hover:bg-white/15 hover:text-white"><GripVertical size={14} /></button>
                <span className="font-mono text-[10px] px-1 text-slate-300">{sel.tag}</span>
                <Tool icon={ChevronsUp} title="Selecionar pai" testid="el-parent" onClick={ed.ops.parent} />
                <Tool icon={ArrowUp} title="Mover para cima" testid="el-up" onClick={ed.ops.up} />
                <Tool icon={ArrowDown} title="Mover para baixo" testid="el-down" onClick={ed.ops.down} />
                <Tool icon={Type} title="Editar texto" testid="el-edit" onClick={ed.ops.edit} />
                <Tool icon={Copy} title="Duplicar" testid="el-duplicate" onClick={ed.ops.duplicate} />
                <Tool icon={Sparkles} title="Pedir para a IA editar este elemento" testid="el-ai" onClick={onAskAI} />
                <Tool icon={Trash2} title="Excluir" testid="el-delete" onClick={ed.ops.remove} danger />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
